import styled, { createGlobalStyle } from "styled-components";
import useInfiniteScroll from "react-infinite-scroll-hook";
import { loadRemoteModule } from "@coxauto-ui/core/module-federation";
import { CcxmReusableBridgeBarFriendlySlidingDrawer } from "@coxauto-ui/ccxm/reusable/bridge-bar-friendly-sliding-drawer";
// eslint-disable-next-line @nx/enforce-module-boundaries
import "antd/dist/antd.css";
import React, {
  ComponentType,
  LazyExoticComponent,
  ReactNode,
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { CcxmInsightsPillFilters } from "@coxauto-ui/ccxm-insights-filters-pill-filters";
import {
  ConsumerType,
  CxmEnvironmentValues,
  FilterSequence,
  MatrixFilters,
  OptionFilters,
  Paging,
  TextFilters
} from "@coxauto-ui/cxm/interfaces";
import { useInsightsListMode } from "@coxauto-ui/ccxm/util/use-insights-list-mode";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import {
  configurationActions,
  getConfigurationState,
  getUserProfileState
} from "@coxauto-ui/ccxm/store";
import { CcxmInsightsFiltersSearchFilter } from "@coxauto-ui/ccxm/insights/filters/search-filter";
import classNames from "classnames";
// eslint-disable-next-line @nx/enforce-module-boundaries
import { ConversationProps } from "@coxauto-ui/communications-customer-conversation";

import { XMarkIcon } from "@interstate/components/Icons/XMarkIcon";
import { Button } from "@interstate/components/Button";
import { Spinner } from "@interstate/components/Spinner";
import { FunnelIcon } from "@interstate/components/Icons/FunnelIcon";
import {
  RoutingContext,
  useRouting,
  useRoutingLocation,
  useRoutingParams,
  useRoutingSearchParams
} from "@coxauto-ui/cxm/routing";
import authClient from "@coxauto-ui/core/auth/get-auth-client-crw";
import { Badge } from "@interstate/components/Badge";
import CcxmInsightsListRealTime from "./ccxm-insights-list-real-time";
import { CustomerCardProps, DefaultTab } from "@coxauto-ui/customer-interfaces";
import { CcxmInsightsFiltersSlidingDrawerFilters } from "@coxauto-ui/ccxm/insights/filters/sliding-drawer-filters";
import { NewDealDrawer } from "./new-deal-drawer";

import { useCxmFlags } from "@coxauto-ui/core/third-party/launch-darkly";
import { getEnvConfig } from "@coxauto-ui/core/get-env";
import { CombinedBusinessCardProps } from "@coxauto-ui/customer-interfaces";
import { useCoxAnalytics } from "@coxauto-ui/ccxm/util/use-cox-analytics-page-tracking";
import { getFilterState } from "@coxauto-ui/util/get-selected-filter-categories";
import { selectAllDealInsightsRecords } from "@coxauto-ui/ccxm/store";

const CustomerCardModule: LazyExoticComponent<
  ComponentType<CustomerCardProps>
> = React.lazy(() => loadRemoteModule("customer", "./CustomerCardModule"));

const CombinedBusinessCardModule: LazyExoticComponent<
  ComponentType<CombinedBusinessCardProps>
> = React.lazy(() =>
  loadRemoteModule("customer", "./CombinedBusinessCardModule")
);

const CustomerConversationModule: LazyExoticComponent<
  ComponentType<ConversationProps>
> = React.lazy(() =>
  loadRemoteModule("communications", "./CustomerConversationModule")
);

const {
  ENV: environment,
  SALES_VIEW_URL: salesViewUrl,
  MANAGER_VIEW_URL: managerViewUrl
} = getEnvConfig<CxmEnvironmentValues>("cxm");

export interface CcxmInsightsListProps {
  children: ReactNode;
  dealerGroupId: string;
  showRealTimeUpdates?: boolean;
  dealerGroupName: string;
  displayVinButton?: boolean;
  error?: string;
  filterSequence: FilterSequence[];
  loading: boolean;
  optionFilters: OptionFilters;
  pageTitle: string;
  paging: Paging;
  showFilters: boolean;
  textFilters: TextFilters;
  matrixFilters?: MatrixFilters;
  updateNotifications?: ReactNode;
  onFilterChange: (
    optionFiltersToApply: OptionFilters,
    textFiltersToApply: TextFilters,
    matrixFiltersToApply?: MatrixFilters
  ) => void;
  onLoadMore: () => void;
}

const ToastOverrideStyle = createGlobalStyle`
.Toastify__toast-container {
    transform: translate(0, 28px) !important;
    width: 550px !important;

    @media (max-width: 768px) {
        transform: translate(0, 48px) !important;
        width: 370px !important;
    }
  }
  .Toastify__toast-theme--light {
    background-color: rgb(255, 255, 255);
    border-color: rgb(49, 138, 211);
    border-width: 1px 1px 1px 8px;
    border-style: solid;
    border-radius: 4px;
    color: rgb(0, 0, 0);
  }
`;

const StyledCcxmInsightsList = styled.div`
  position: relative;
  height: calc(100% - 35px);

  @supports (-webkit-touch-callout: none) {
    /* CSS specific to iOS devices */
    height: -webkit-fill-available;
  }

  &.viewing-as {
    max-height: calc(100% - 33px - 35px);

    @supports (-webkit-touch-callout: none) {
      /* CSS specific to iOS devices */
      height: calc(-webkit-fill-available - 33px - 35px);
    }
  }

  #sliding-drawer-filters {
    min-width: 300px;
    max-width: 100vw;

    .cx-drawer__panel {
      overflow: auto;
      overscroll-behavior: none;
    }

    #pill-filters-wrapper {
      margin: 0;
      flex-wrap: wrap;
    }
  }

  .cx-drawer__background {
    z-index: 90;
  }

  #occ-inbox-sliding-drawer {
    #SlidingDrawerCloseBtn {
      z-index: 1;
    }

    #StyledCommonCommunicationsConversationListPageHeaderCustomerName {
      max-width: calc(100% - 38px);
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
`;

const StyledContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: min-content 1fr;
  border-radius: 4px;
  background-color: #fff;
  height: 100%;

  &.desktopList,
  &.tabletList,
  &.tabletCustomer,
  &.mobileList,
  &.mobileCustomer {
    grid-template-areas:
      "header"
      "cards";
  }

  &.desktopCustomer {
    grid-template-columns: 500px 1fr;
    grid-template-areas:
      "header header"
      "cards customer";
  }
`;

const StyledHeader = styled.div`
  grid-area: header;
  border-top: 1px solid #b2b6bf;
  padding: 16px 24px;
  border-bottom: solid 4px #003468;
  margin-bottom: 16px;
  background: #fff;

  h1 {
    margin-bottom: 16px;
    font-size: 24px;

    .dealer-group-totalItems {
      color: #1890ff;
    }
  }

  .toolbar {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    align-items: center;
    gap: 8px;
  }

  .filter-wrapper {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    flex-grow: 1;
  }

  .create-new-deal-button {
    flex-shrink: 0;
    align-self: flex-start;
    max-width: 100%;
  }

  &.mobileCustomer {
    max-width: 100%;
    box-sizing: border-box;
  }
`;

const StyledInsightCards = styled.div`
  position: relative;
  grid-area: cards;
  height: auto;
  overflow: auto;
  overscroll-behavior: none;
  margin: 0 24px;

  &.desktopCustomer {
    margin: 0 8px 8px 16px;
  }
`;

const StyledLockScroll = createGlobalStyle`
  body {
    overflow: hidden;
  }
`;

const StyledConsumerDetails = styled.div<{ spacingFromTop: number }>`
  &.desktopCustomer {
    grid-area: customer;
    margin: 0 16px 16px 8px;
    height: 100%;
    max-height: calc(100vh - 150px); //Increased buffer to eliminate scroll bar
    overflow: auto;
    overscroll-behavior: none;
    display: grid;
    grid-template-columns: 1fr;
    grid-template-rows: 48px 1fr;
    grid-template-areas:
      "consumerdetailsheader"
      "customercardwrapper";
  }

  &.inline-occ-inbox {
    display: grid;
    grid-template-columns: 1fr 500px;
    grid-template-rows: 48px 1fr;
    grid-template-areas:
      "consumerdetailsheader consumerdetailsheader"
      "customercardwrapper customerconversationwrapper";
  }

  &.tabletCustomer,
  &.mobileCustomer {
    position: fixed;
    top: 35px;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: #fff;
    padding: 16px;
    overflow-y: scroll;
    overflow-x: hidden;
    overscroll-behavior: none;
    z-index: 4;
    max-width: 100vw;
    width: 100%;
    overflow-x: auto;
  }

  top: ${({ spacingFromTop }) =>
    spacingFromTop > 0 ? `${spacingFromTop}px` : 0};
  ${({ spacingFromTop }) =>
    spacingFromTop > 0 && "border-top: 1px solid #b2b6bf;"};
`;

const StyledConsumerDetailsHeader = styled.div`
  grid-area: consumerdetailsheader;
  &.desktopCustomer {
    position: relative;
    display: flex;
    justify-content: flex-end;
    align-items: center;
    background-color: #0d2245;
    color: #fff;
    border-top-right-radius: 8px;
    border-top-left-radius: 8px;
    height: 48px;

    #customer-record-title {
      color: #fff;
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      font-weight: 400;
    }

    .interstate-x-mark-icon {
      margin-right: 16px;
      cursor: pointer;
    }
  }

  &.tabletCustomer,
  &.mobileCustomer {
    display: flex;
    justify-content: space-between;
  }
`;

const StyledCustomerCardWrapper = styled.div`
  grid-area: customercardwrapper;
  overflow: hidden;
  overflow-y: auto;
  height: 100%;
  min-height: 0;

  @media (max-width: 1279px) {
    max-height: calc(100vh - 200px);
  }

  #customer-card-container {
    height: auto;
  }
`;

const StyledCustomerConversationWrapper = styled.div`
  grid-area: customerconversationwrapper;
  position: relative;
  border-right: 1px solid #e4e9ef;
  overflow: hidden;
`;

const StyledErrorMessage = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;
`;

const StyledPillWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
`;

const StyledDivider = styled.hr`
  margin: 4px 0 16px;
`;

const StyledLoadingSpinnerContainer = styled.div`
  background-color: #6d727a;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 60px;

  /* Target the SVG inside the spinner to make it white */
  svg {
    color: white;
  }
`;

const StyledH1 = styled.h1`
  display: flex;
  flex-direction: row;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
`;

export function CcxmInsightsList({
  children,
  dealerGroupId,
  dealerGroupName,
  displayVinButton = true,
  error,
  filterSequence,
  loading,
  matrixFilters,
  showRealTimeUpdates = false,
  optionFilters,
  pageTitle,
  paging,
  showFilters,
  textFilters,
  updateNotifications,
  onFilterChange,
  onLoadMore
}: CcxmInsightsListProps) {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const navigate = useRouting();
  const { pathname } = useRoutingLocation();
  const { customerId } = useRoutingParams();
  const [searchParams] = useRoutingSearchParams();
  const {
    // eslint-disable-next-line @cspell/spellchecker
    "nx.cxm.show-addadeal-button": showNewDealButton,
    "cxm.use-new-create-deal-target.enable": newCreateDealTargetEnabled
  } = useCxmFlags();

  const {
    isModule,
    selectedCustomerId,
    selectedDealId,
    selectedConsumerType,
    selectedMultiDealCustomerId,
    defaultCustomerTab,
    setSelectedCustomerId,
    setSelectedDealId,
    setSelectedConsumerType,
    setSelectedMultiDealCustomerId
  } = useContext(RoutingContext);
  const dispatch = useDispatch();
  const [spacingFromTop, setSpacingFromTop] = useState(
    window.scrollY > 35 ? 0 : 35 - window.scrollY
  );
  const [hasInlineOccInbox, setHasInlineOccInbox] = useState(
    window.innerWidth >= 1500
  );

  const { viewportMode } = useSelector(getConfigurationState, shallowEqual);
  const { viewAsUsername, bridgeUserName, bridgeUserId, products } =
    useSelector(getUserProfileState, shallowEqual);
  const dealRecords = useSelector(selectAllDealInsightsRecords, shallowEqual);
  const [showNewDealDrawer, setShowNewDealDrawer] = useState(false);
  const [showSlidingDrawerFilters, setShowSlidingDrawerFilters] =
    useState(false);
  const listMode = useInsightsListMode();
  const [userDisplayName, setUserDisplayName] = useState<string | null>(null);
  // use a regular variable because we dont want to add extra renders
  let visibleId: string | null = null;
  const [scrollingId, setScrollingId] = useState<string>("");

  const { pushEvent } = useCoxAnalytics();

  // Helper function to determine which filters are currently applied
  const filterState = useMemo(() => {
    return getFilterState({
      optionFilters,
      textFilters,
      matrixFilters
    });
  }, [optionFilters, textFilters, matrixFilters]);

  const scrollCallback = () => {
    handleScroll();
    setSpacingFromTop(window.scrollY > 35 ? 0 : 35 - window.scrollY);
  };

  const resizeCallback = () => {
    const showInlineOccInbox = window.innerWidth >= 1500;
    if (hasInlineOccInbox !== showInlineOccInbox) {
      setHasInlineOccInbox(showInlineOccInbox);
    }
  };

  const [sentryRef, { rootRef }] = useInfiniteScroll({
    loading,
    hasNextPage:
      paging.pageCount > paging.pageNumber || paging.pageNumber === 0,
    onLoadMore,
    rootMargin: "0px 0px 150px 0px"
  });

  useEffect(() => {
    if (!isModule) {
      const urlDealId = searchParams.get("dealId");
      const selectedConsumerTypeParam = searchParams.get(
        "selectedConsumerType"
      ) as ConsumerType | null;
      const urlMultiDealCustomerId = searchParams.get("multiDealConsumerId");
      if (customerId && selectedCustomerId !== customerId) {
        setSelectedCustomerId(customerId);
      }
      if (urlDealId && selectedDealId !== urlDealId) {
        setSelectedDealId(urlDealId);
      }
      if (
        urlMultiDealCustomerId &&
        selectedMultiDealCustomerId !== urlMultiDealCustomerId
      ) {
        setSelectedMultiDealCustomerId(urlMultiDealCustomerId);
      }
      if (
        selectedConsumerTypeParam &&
        selectedConsumerType !== selectedConsumerTypeParam
      ) {
        setSelectedConsumerType(selectedConsumerTypeParam);
      }
    }
  }, [
    customerId,
    isModule,
    searchParams,
    selectedCustomerId,
    selectedDealId,
    selectedConsumerType,
    selectedMultiDealCustomerId,
    setSelectedCustomerId,
    setSelectedDealId,
    setSelectedMultiDealCustomerId,
    setSelectedConsumerType
  ]);

  useEffect(() => {
    window.addEventListener("resize", resizeCallback, true);
    return () => {
      window.removeEventListener("resize", resizeCallback, true);
    };
  });

  useEffect(() => {
    window.addEventListener("scroll", scrollCallback, true);

    return () => {
      window.removeEventListener("scroll", scrollCallback, true);
    };
  });

  useEffect(() => {
    dispatch(configurationActions.setCustomerId(customerId || null));
  }, [customerId, dispatch]);

  useEffect(() => {
    if (loading === false && paging.pageNumber === 1) {
      scrollRef.current?.scrollTo({ top: 0, left: 0 });
    }
  }, [loading, paging.pageNumber]);

  useEffect(() => {
    (async () => {
      const claims = await authClient.getIdTokenClaims();
      setUserDisplayName(claims?.name || null);
    })();
  }, []);

  const toggleSlidingDrawerFilters = () => {
    setShowSlidingDrawerFilters(toggle => {
      // Cox Analytics event when opening filters
      if (!toggle) {
        pushEvent({
          event: {
            action: "Click",
            element: "Filter Button",
            intention: "Open Filters",
            location: "Deal Central, Deal List",
            tagIndex: "dl-006"
          }
        });
      }
      return !toggle;
    });
  };

  const toggleNewDealDrawer = () => {
    setShowNewDealDrawer(toggle => !toggle);
  };

  const handleNewDealClick = () => {
    // Cox Analytics event for Create New Deal button click
    pushEvent({
      event: {
        action: "Click",
        element: "Create New Deal Button",
        intention: "Create new deal with customer or vehicle information",
        location: "Deal Central, Deal List",
        tagIndex: "dl-008"
      }
    });

    if (newCreateDealTargetEnabled) {
      const url = canDesk ? managerViewUrl : salesViewUrl;
      window.open(url, "_blank", "noopener,noreferrer");
    } else if (
      showNewDealButton ||
      (viewAsUsername && viewAsUsername.trim() !== "")
    ) {
      toggleNewDealDrawer();
    }
  };

  const handleFilterChange = (
    optionFiltersToApply: OptionFilters,
    textFiltersToApply: TextFilters,
    matrixFiltersToApply?: MatrixFilters
  ) => {
    onFilterChange(
      optionFiltersToApply,
      textFiltersToApply,
      matrixFiltersToApply
    );
  };

  useEffect(() => {
    if (selectedDealId && !scrollingId) {
      setScrollingId(selectedDealId);
    }
    /// no add scrollingId to dependency array because create infinite loop
  }, [selectedDealId]);

  useEffect(() => {
    if (scrollRef.current && scrollingId && !selectedDealId) {
      scrollToDealId(scrollingId);
    } else if (scrollRef.current && selectedDealId === scrollingId) {
      scrollToDealId(`close-my-deal-card-${scrollingId}`);
    }
  }, [selectedDealId]);

  const scrollToDealId = (id: string) => {
    const targetElement = document.getElementById(id);
    if (targetElement) {
      targetElement.scrollIntoView();
      setScrollingId("");
    }
  };

  const [canDesk, setCanDesk] = useState<boolean>(false);
  useEffect(() => {
    const dealers = products["retail360"]?.dealers || [];
    const dealer = dealers.length === 1 ? dealers[0] : null;
    if (dealer && dealer?.deskingManager) {
      setCanDesk(true);
    } else {
      setCanDesk(false);
    }
  }, [products]);

  // Track when Customer Record opens
  useEffect(() => {
    if (customerId) {
      // Helper function to calculate results rank from card position
      const getResultsRank = () => {
        if (selectedDealId) {
          // Find the card element with the selected deal ID
          const cardElement = document.querySelector(
            `[id="close-my-deal-card-${selectedDealId}"]`
          );
          if (cardElement) {
            // Extract the index from the data-testid attribute
            const testId = cardElement.getAttribute("data-testid");
            if (testId) {
              // Extract the number from the end of the data-testid
              const match = testId.match(/close-my-deal-card-(\d+)$/);
              if (match && match[1]) {
                return parseInt(match[1], 10);
              }
            }
          }
        }
        return null; // Default fallback
      };

      // Find the corresponding deal record to get vehicle information
      const selectedDeal = dealRecords.find(
        deal => deal.dealId === selectedDealId
      );
      const vehicleInfo = selectedDeal?.sellingVehicle;

      // Cox Analytics event for Customer Record opening
      pushEvent({
        deal: {
          consumerId: customerId,
          dealId: selectedDealId || null
        },
        event: {
          action: "Click",
          element: "Customer Card Link",
          intention: "Open Customer Information",
          location: "Deal Central, Deal List",
          tagIndex: "dl-007"
        },
        search: {
          acceptedOffers: filterState.hasAcceptedOffersFilter,
          date: filterState.hasDateFilter,
          dealer: filterState.hasDealerFilter,
          dealPaymentMethod: filterState.hasDealPaymentMethodFilter,
          dealStages: filterState.hasDealStagesFilter,
          dealStatus: filterState.hasDealStatusFilter,
          employee: filterState.hasEmployeeFilter,
          make: filterState.hasMakeFilter,
          resultsRank: getResultsRank(),
          vehicleType: filterState.hasVehicleTypeFilter
        },
        vehicle: {
          dealerStockNumber: vehicleInfo?.stockNumber || null,
          make: vehicleInfo?.make || null,
          model: vehicleInfo?.model || null,
          year: vehicleInfo?.year || null
        }
      });
    }
  }, [customerId, selectedDealId, pushEvent, filterState, dealRecords]);

  const customerDetailsCallback = useCallback(() => {
    setSelectedDealId(null);
    setSelectedCustomerId(null);
    setSelectedConsumerType(null);
    searchParams.delete("dealId");
    searchParams.delete("selectedConsumerType");
    const path =
      pathname.split(customerId || "")[0] + "?" + searchParams.toString();
    navigate(path, { replace: false });
    setScrollingId(visibleId || "");
  }, [
    customerId,
    navigate,
    pathname,
    searchParams,
    setSelectedConsumerType,
    setSelectedCustomerId,
    setSelectedDealId,
    visibleId
  ]);

  const user = useMemo(() => {
    return {
      key: bridgeUserId,
      name: bridgeUserName,
      custom: {
        dealerGroupId,
        dealerGroupName
      }
    };
  }, [bridgeUserId, bridgeUserName, dealerGroupId, dealerGroupName]);

  const occConfig = useMemo(() => {
    return {
      username: userDisplayName ?? "",
      disableReply: viewAsUsername !== null && viewAsUsername.trim() !== ""
    };
  }, [userDisplayName, viewAsUsername]);

  const onPageReload = () => {
    window.location.reload();
  };

  const setRefs = useCallback(
    (element: HTMLDivElement | null) => {
      scrollRef.current = element;
      rootRef(element);
    },
    [rootRef]
  );

  const handleScroll = () => {
    if (!scrollRef?.current) return;
    const items = scrollRef.current.querySelectorAll(".close-my-deals-card");
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      const rect = item.getBoundingClientRect();
      const listRect = scrollRef.current?.getBoundingClientRect();
      if (
        listRect &&
        rect.top >= listRect.top &&
        rect.bottom <= listRect.bottom
      ) {
        visibleId = item.id;
        break;
      }
    }
  };

  const customerDetails = useMemo(() => {
    if (selectedConsumerType === "Business") {
      return (
        <div data-testid="combined-business-card-wrapper">
          <CombinedBusinessCardModule
            consumerOrBusinessEntityId={customerId || ""}
            environment={environment}
            mode="business-entity"
            viewportMode={viewportMode}
          />
        </div>
      );
    }

    return (
      <CustomerCardModule
        commonConsumerID={customerId || ""}
        dealId={selectedDealId || ""}
        defaultTab={(defaultCustomerTab as DefaultTab) || "information"}
        displayVinButton={displayVinButton}
        doneCallback={customerDetailsCallback}
        editCapability="enabled"
        environment={environment}
        getAuthOverride={() => authClient.getAuth()}
        ldUser={user}
        occConfig={hasInlineOccInbox ? undefined : occConfig}
        viewportMode={viewportMode}
      />
    );
  }, [
    customerId,
    viewportMode,
    selectedDealId,
    defaultCustomerTab,
    displayVinButton,
    hasInlineOccInbox,
    selectedConsumerType,
    occConfig,
    user,
    customerDetailsCallback
  ]);

  return (
    <StyledCcxmInsightsList
      className={classNames({ "viewing-as": viewAsUsername !== "" })}
    >
      <ToastOverrideStyle />
      {dealerGroupId !== "" && (
        <>
          <StyledContentGrid
            className={listMode}
            data-testid="ccxm-insights-list-content-grid"
          >
            <StyledHeader data-testid="dealer-group-header">
              <div>
                <StyledH1>
                  <span>{dealerGroupName}</span>{" "}
                  <Badge
                    data-count={paging.totalItems}
                    data-testid="insight-list-total-items-count"
                  >
                    Total {pageTitle} {paging.totalItems}
                  </Badge>
                  {showRealTimeUpdates && updateNotifications && (
                    <CcxmInsightsListRealTime>
                      {updateNotifications}
                    </CcxmInsightsListRealTime>
                  )}
                </StyledH1>
              </div>
              <div className="toolbar">
                <div className="filter-wrapper">
                  {showFilters && (
                    <Button
                      buttonStyle="secondary"
                      className="add-filter-pill"
                      data-testid="add-filter-button"
                      id="inline-pill-filter-add-filter-dropdown"
                      size={"small"}
                      startIcon={<FunnelIcon />}
                      onClick={toggleSlidingDrawerFilters}
                    >
                      Filters
                    </Button>
                  )}
                  {showFilters && (
                    <CcxmInsightsFiltersSearchFilter
                      className="search-filter"
                      optionFilters={optionFilters}
                      textFilters={textFilters}
                      onChange={handleFilterChange}
                    />
                  )}
                  <CcxmInsightsPillFilters
                    clearButtonDisplay="separate"
                    filterSequence={filterSequence}
                    listMode={listMode}
                    loading={loading}
                    matrixFilters={matrixFilters}
                    optionFilters={optionFilters}
                    textFilters={textFilters}
                    onChange={handleFilterChange}
                  />
                </div>
                {(showNewDealButton ||
                  newCreateDealTargetEnabled ||
                  (viewAsUsername && viewAsUsername.trim() !== "")) && (
                  <Button
                    className="create-new-deal-button"
                    data-testid="ccxm-insights-list-create-new-deal-button"
                    size={"medium"}
                    onClick={handleNewDealClick}
                  >
                    Create New Deal
                  </Button>
                )}
              </div>
            </StyledHeader>
            <StyledInsightCards
              data-testid="ccxm-insights-list-styled-insights-cards"
              ref={setRefs}
            >
              {error ? (
                <StyledErrorMessage>
                  <span data-testid="ccxm-insights-list-error-message">
                    Sorry, something went wrong. Please try again later.
                  </span>
                  <Button
                    data-testid="ccxm-insights-list-error-reload-button"
                    id="ccxm-insights-list-error-reload-button"
                    onClick={onPageReload}
                  >
                    Reload
                  </Button>
                </StyledErrorMessage>
              ) : (
                <>
                  {children}
                  {showFilters && (
                    <div
                      data-testid="insights-list-load-more-ref"
                      ref={sentryRef}
                    />
                  )}
                  {loading === true && (
                    <StyledLoadingSpinnerContainer>
                      <Spinner
                        aria-label="Loading deals"
                        id="loadingIndicatorWhite"
                      />
                    </StyledLoadingSpinnerContainer>
                  )}
                </>
              )}
            </StyledInsightCards>
            {customerId && (
              <StyledConsumerDetails
                className={classNames([
                  hasInlineOccInbox && selectedConsumerType !== "Business"
                    ? "inline-occ-inbox"
                    : "customer-card-occ-inbox",
                  listMode
                ])}
                data-testid="consumer-details-wrapper"
                role={listMode !== "desktopCustomer" ? "dialog" : undefined}
                spacingFromTop={spacingFromTop}
              >
                <StyledConsumerDetailsHeader className={listMode}>
                  <h1 id="customer-record-title">Customer Record</h1>
                  <div id="customer-record-modal-close-button">
                    <XMarkIcon
                      data-testid="customer-record-modal-close-button"
                      onClick={customerDetailsCallback}
                    />
                  </div>
                </StyledConsumerDetailsHeader>
                <Suspense fallback={<div>Loading Customer Card...</div>}>
                  <StyledCustomerCardWrapper>
                    {customerDetails}
                  </StyledCustomerCardWrapper>
                </Suspense>
                {hasInlineOccInbox && selectedConsumerType !== "Business" && (
                  <Suspense
                    fallback={<div>Loading Customer Conversations...</div>}
                  >
                    <StyledCustomerConversationWrapper>
                      <CustomerConversationModule
                        customerId={customerId || ""}
                        getAuthOverride={authClient.getAuth}
                        occConfig={occConfig}
                        title="CUSTOMER INBOX"
                      />
                    </StyledCustomerConversationWrapper>
                  </Suspense>
                )}
              </StyledConsumerDetails>
            )}
          </StyledContentGrid>
          {(showSlidingDrawerFilters === true ||
            listMode === "tabletCustomer" ||
            listMode === "mobileCustomer") && <StyledLockScroll />}
          <CcxmReusableBridgeBarFriendlySlidingDrawer
            header={"Filters"}
            id="sliding-drawer-filters"
            panelWidth="367px"
            show={showSlidingDrawerFilters}
            onHide={toggleSlidingDrawerFilters}
          >
            <StyledPillWrapper data-testid="pill-filters-wrapper">
              <CcxmInsightsPillFilters
                filterSequence={filterSequence}
                isInline={false}
                loading={loading}
                matrixFilters={matrixFilters}
                optionFilters={optionFilters}
                textFilters={textFilters}
                onChange={handleFilterChange}
              />
            </StyledPillWrapper>
            <CcxmInsightsFiltersSlidingDrawerFilters
              disabled={loading}
              filterSequence={filterSequence}
              matrixFilters={matrixFilters}
              optionFilters={optionFilters}
              textFilters={textFilters}
              onChange={handleFilterChange}
            />
            <StyledDivider id="checkbox-filter-divider" />
          </CcxmReusableBridgeBarFriendlySlidingDrawer>
          {(showNewDealButton ||
            (viewAsUsername && viewAsUsername.trim() !== "")) && (
            <NewDealDrawer
              show={showNewDealDrawer}
              onHide={toggleNewDealDrawer}
            />
          )}
        </>
      )}
    </StyledCcxmInsightsList>
  );
}

export default CcxmInsightsList;
